import { useMemo, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { ChevronRight, Plus, Search, Sparkles } from "lucide-react";
import { de, normalizeName, type Recipe } from "@ainkauf/core";
import { allRecipes, createRecipe, ingredientCounts } from "@/db/repository";
import { useNavigate } from "@/lib/router";
import { RecipeCover } from "./RecipeCover";
import { RecipeImportSheet } from "./RecipeImportSheet";
import { RecipeOrigin } from "./RecipeOrigin";

/** Ab hier lohnt sich ein Suchfeld. Darunter sieht man alles auf einen Blick. */
const SUCHE_AB = 9;

/**
 * Die Sammlung der Rezepte.
 *
 * Zwei Wege hinein: selbst anlegen oder übernehmen lassen. Der zweite ist der
 * häufigere, deshalb steht er oben und der leere Zustand erklärt ihn.
 */
export function RecipeList() {
  const go = useNavigate();
  const [neu, setNeu] = useState("");
  const [suche, setSuche] = useState("");
  const [uebernahme, setUebernahme] = useState(false);

  const rezepte = useLiveQuery(() => allRecipes(), [], [] as Recipe[]);
  const zutatenzahl = useLiveQuery(() => ingredientCounts(), [], new Map<string, number>());

  // Gefiltert wird hier, nicht in Dexie: Eine Familiensammlung ist klein, und
  // so bleibt die Liste beim Tippen stehen, statt bei jedem Zeichen neu zu laden.
  const gefiltert = useMemo(() => {
    const gesucht = normalizeName(suche);
    if (gesucht.length === 0) return rezepte;
    return rezepte.filter((rezept) => rezept.normalized_name.includes(gesucht));
  }, [rezepte, suche]);

  async function anlegen() {
    const name = neu.trim();
    if (name.length === 0) return;
    setNeu("");
    const rezept = await createRecipe(name);
    go(`/rezept/${rezept.id}`);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2">
        <input
          value={neu}
          onChange={(event) => {
            setNeu(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") void anlegen();
          }}
          placeholder={de.recipes.namePlaceholder}
          aria-label={de.recipes.nameLabel}
          data-testid="recipe-name"
          className="min-h-[52px] min-w-0 flex-1 rounded-[var(--radius-lg)] bg-card px-4 text-base text-ink shadow-[var(--shadow-card)] outline-none focus:ring-2 focus:ring-accent"
        />
        <button
          type="button"
          onClick={() => void anlegen()}
          aria-label={de.recipes.create}
          data-testid="recipe-create"
          className="inline-flex size-[52px] shrink-0 items-center justify-center rounded-[var(--radius-lg)] bg-accent text-white shadow-[var(--shadow-card)] transition-transform active:scale-95"
        >
          <Plus size={22} strokeWidth={2.25} aria-hidden />
        </button>
      </div>

      <button
        type="button"
        onClick={() => {
          setUebernahme(true);
        }}
        data-testid="recipe-import"
        className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-[var(--radius-lg)] bg-paper-2 px-4 font-semibold text-ink-2 transition-transform active:scale-[0.99]"
      >
        <Sparkles size={18} strokeWidth={1.75} aria-hidden />
        {de.recipes.importTitle}
      </button>

      {rezepte.length >= SUCHE_AB && (
        <label className="relative flex items-center">
          <Search size={18} strokeWidth={1.75} aria-hidden className="pointer-events-none absolute left-4 text-ink-3" />
          <input
            value={suche}
            onChange={(event) => {
              setSuche(event.target.value);
            }}
            type="search"
            placeholder={de.spaisekammer.search}
            aria-label={de.spaisekammer.search}
            data-testid="recipe-search"
            className="min-h-[52px] w-full rounded-[var(--radius-lg)] bg-paper-2 pl-11 pr-4 text-base text-ink outline-none focus:ring-2 focus:ring-accent"
          />
        </label>
      )}

      {rezepte.length === 0 ? (
        <p className="karte px-5 py-10 text-center text-ink-2" data-testid="recipes-empty">
          {de.recipes.empty}
          <span className="mt-1 block text-sm">{de.recipes.emptyHint}</span>
        </p>
      ) : gefiltert.length === 0 ? (
        <p className="karte px-5 py-10 text-center text-ink-2" data-testid="recipes-no-match">
          {de.spaisekammer.searchEmpty}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {gefiltert.map((rezept, i) => (
            <li key={rezept.id} className="auftritt" style={{ ["--takt" as string]: `${String(Math.min(i * 30, 240))}ms` }}>
              <button
                type="button"
                onClick={() => {
                  go(`/rezept/${rezept.id}`);
                }}
                data-testid="recipe-row"
                data-name={rezept.name}
                data-source={rezept.source}
                className="karte flex min-h-[72px] w-full items-center gap-3 px-4 text-left transition-transform active:scale-[0.99]"
              >
                <RecipeCover recipe={rezept} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-display text-lg font-extrabold tracking-tight text-ink">{rezept.name}</span>
                  <span className="num block truncate text-sm text-ink-2">
                    {de.recipes.ingredientCount(zutatenzahl.get(rezept.id) ?? 0)}, {de.week.servingsOf(rezept.servings)}
                  </span>
                  <RecipeOrigin recipe={rezept} />
                </span>
                <ChevronRight size={18} strokeWidth={1.5} className="shrink-0 text-ink-3" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      {uebernahme && (
        <RecipeImportSheet
          onDone={(id) => {
            setUebernahme(false);
            if (id !== null) go(`/rezept/${id}`);
          }}
        />
      )}
    </div>
  );
}
